import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { InventoryItemDetail, LaunchDarklyService, UserProfile, DateUtilsService, AuctionItemState, DetailsState, BookValueSpreadProviderName, BookProviderService } from 'remarketing-angular-library';
import { DateTimeFormat } from 'remarketing-angular-library/enums/dateTime.enum';
import { get } from 'lodash';
import { SaComponent, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DatalistItemComponent, TooltipComponent } from 'remarketing-ui-components-lib';
import {
  CollapseCardComponent
} from '../../../sell-shared/components/collapse-card/collapse-card.component';
import {
  ImagesCountComponent
} from '../../../sell-shared/components/images-count/images-count.component';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'inventory-item-card',
    templateUrl: './inventory-item-card.component.html',
    styleUrls: ['./inventory-item-card.component.scss'],
    providers: [DecimalPipe],
    imports: [
        TranslocoDirective,
        CommonModule,
        DatalistItemComponent,
        CollapseCardComponent,
        ImagesCountComponent,
        TooltipComponent,
        RouterLink
    ]
})
export class InventoryItemCardComponent extends SaComponent implements OnInit, OnDestroy {
  @Select(AuctionItemState.auctionItem) auctionItem$: Observable<any>;

  inventoryItem: any;
  auctionItem: any;

  collapsed = false;

  userProfile: UserProfile;

  showAuctionStartTime = false;
  showAuctionEndTime = false;
  showAuctionExtendTime = false;
  isAuctionItemActive = false;

  spreadToBook: number;
  bookValues: Array<any> = [];

  isMobile: boolean;

  translations = {
    distanceUnit: '',
    notAvailable: '',
    inspectionSeriesLabelText: ''
  };

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.detectIfMobile();
  }

  constructor(
    private store: Store,
    private _decimalPipe: DecimalPipe,
    private readonly bookProvierService: BookProviderService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService,
    private readonly dateUtilService: DateUtilsService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
    this.bookValues = this.bookProvierService.bookValues;
  }

  get title() {
    return `${this.detail('modelYear')} ${this.detail('make')} ${this.detail('model')} ${this.detail('trim')} ${
      this.driveTrain
    } `;
  }

  detail(key: keyof InventoryItemDetail): string {
    return get(this.itemDetails, key, '');
  }

  get itemDetails(): any | {} {
    return get(this.inventoryItem, 'itemDetail', {});
  }

  get driveTrain() {
    return get(this.inventoryItem, 'itemDetail.powerTrain.driveTrainAbbrev', '');
  }

  get odometerDisplay() {
    const odometer = get(this.inventoryItem, 'itemDetail.odometer');
    return odometer
      ? `${this._decimalPipe.transform(odometer)} ${this.translations.distanceUnit}`
      : this.translations.notAvailable;
  }

  get bidStatus() {
    if (this.auctionItem && !this.auctionItem.openingPrice && this.isAuctionItemActive) {
      if (this.auctionItem.auctionWindow) {
        return 'BIDDING IN PROGRESS';
      } else {
        return 'BIDDING ENDED';
      }
    } else {
      return undefined;
    }
  }

  get isTenantBadgeEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-tenantBadgeEnabled', false);
  }

  get isInspectionSeriesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inspectionSeriesEnabled', false);
  }

  viewCount() {
    return this.inventoryItem ? get(this.inventoryItem, 'hitCount').toString() : this.translations.notAvailable;
  }

  tenantBadgeName() {
    return this.auctionItem ? get(this.auctionItem, 'auctionTenant').toString() : this.translations.notAvailable;
  }

  daysRemainingAuction() {
    return this.auctionItem ? get(this.auctionItem, 'daysLeftInAuction').toString() : this.translations.notAvailable;
  }

  get(path: string, otherwise: any = 'N/A'): string {
    return get(this.inventoryItem, path, otherwise);
  }

  sellerName() {
    return this.inventoryItem ? get(this.inventoryItem, 'seller.name') : this.translations.notAvailable;
  }

  ngOnInit() {
    this.getTranslations();
    this.store
      .select(DetailsState.inventoryItem)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(item => {
        this.inventoryItem = item;
      });

    this.store
      .select(AuctionItemState.auctionItem)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(auctionItem => {
        this.auctionItem = auctionItem;
        this.auctionItem ? this.auctionTimeFlagcheck() : null;
        this.auctionItem ? this.checkAuctionItemActive() : null;
      });

    this.userProfile = this.store.selectSnapshot(state => {
      if (state.user) {
        return state.user.userProfile;
      } else {
        return null;
      }
    });

    // this.imgUrlLarge = this.firstImg();
    this.detectIfMobile();
  }

  getTranslations() {
    const keyParams = {
      common: {},
      'sellModule.inventoryItem.inventoryItemCard': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (translate: any[]) => {
          this.translations.distanceUnit = translate[0].distanceUnit;
          this.translations.notAvailable = translate[0].notAvailable;
          this.translations.inspectionSeriesLabelText = translate[1].labelInspectionSeries;
        },
        error => {
          console.log(error);
        }
      );
  }

  auctionTimeFlagcheck() {
    // reset time flags
    this.showAuctionStartTime = false;
    this.showAuctionEndTime = false;
    this.showAuctionExtendTime = false;
    !this.auctionItem.auctionWindow
      ? (this.showAuctionStartTime = true)
      : this.auctionItem.auctionWindow === 'INPROGRESS'
      ? (this.showAuctionEndTime = true)
      : this.auctionItem.auctionWindow === 'EXTENDED'
      ? (this.showAuctionExtendTime = true)
      : (this.showAuctionStartTime = true);
  }

  toggleCollapseCard(e) {
    this.collapsed = e;
  }

  get bookProvider() {
    if (this.userProfile) {
      const providerName = this.userProfile.userPreferences.defaultBookProvider.providerName === BookValueSpreadProviderName.NADA ?
        'J.D. POWER' : this.userProfile.userPreferences.defaultBookProvider.providerName;
      return (
        providerName +
        ' ' +
        this.userProfile.userPreferences.defaultBookProvider.providerValue
      );
    } else {
      return this.translations.notAvailable;
    }
  }

  getSpreadToBookValue() {
    if (this.spreadToBook) {
      return this.spreadToBook;
    }
    if (!this.userProfile || !this.userProfile.userPreferences) {
      return undefined;
    }
    const defaultBookProvider = this.userProfile.userPreferences.defaultBookProvider.providerName;
    const defaultProviderValue = this.userProfile.userPreferences.defaultBookProvider.providerValue;

    if (this.auctionItem) {
      const bookProviders = this.auctionItem.bookValues.filter(
        bookValue => bookValue.providerName === defaultBookProvider
      );

      if (bookProviders.length > 0) {
        return this.getCurrentValue(bookProviders, defaultBookProvider, defaultProviderValue);
      }
    }

    return undefined;
  }

  getCurrentValue(bookProviders, defaultBookProvider, defaultProviderValue) {
    const filteredProvider = bookProviders[0];
    const providerValue =
      defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.NADA.toLocaleLowerCase() ||
      defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.BLACK_BOOK.toLocaleLowerCase()
        ? `${defaultProviderValue}`.toLocaleLowerCase()
        : null;

    const filteredBookValues = filteredProvider.values.filter(
      value => value.providerValue.toLocaleLowerCase() === providerValue
    );

    if (filteredBookValues.length > 0) {
      this.spreadToBook = filteredBookValues[0].valueAmount;
      return this.spreadToBook;
    }
  }

  ngOnDestroy() {
    this.showAuctionStartTime = false;
    this.showAuctionEndTime = false;
    this.showAuctionExtendTime = false;
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  formatCurrency(currency) {
    if (currency) {
      return this.currencyFormatService.formatCurrency(currency, false);
    }
  }

  get auctionStartTime() {
    const date = this.dateUtilService.localizeDate(this.auctionItem.auctionStartTime);
    const dateAndTime = this.dateUtilService.localizeDateAndTime(this.auctionItem.auctionStartTime, true);

    if (this.auctionItem.auctionItemStatus !== 'ACTIVE') {
      return date;
    }
    return dateAndTime;
  }

  get auctionEndTime() {
    const date = this.dateUtilService.localizeDate(this.auctionItem.auctionEndTime);
    const dateAndTime = this.dateUtilService.localizeDateAndTime(this.auctionItem.auctionEndTime, true);
    if (this.auctionItem.auctionItemStatus !== 'ACTIVE') {
      return date;
    }
    return dateAndTime;
  }

  get auctionExtendedTime() {
    const date = this.dateUtilService.localizeDate(this.auctionItem.auctionExtendEndTime);
    const dateAndTime = this.dateUtilService.localizeDateAndTime(this.auctionItem.auctionExtendEndTime, true);
    if (this.auctionItem.auctionItemStatus !== 'ACTIVE') {
      return date;
    }
    return `${dateAndTime} - Extended`;
  }

  checkAuctionItemActive() {
    this.isAuctionItemActive = false;
    if (this.auctionItem.auctionItemStatus === 'ACTIVE') {
      this.isAuctionItemActive = true;
    }
  }

  detectIfMobile() {
    this.isMobile = window.matchMedia('(max-width: 768px)').matches;
  }
}
